/* eslint-disable react-hooks/exhaustive-deps */

import React, { useRef, useState, useEffect } from "react";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import SeoHead from "../section/SeoHead";
import heroImage from "../../assets/mig5.jpg";
import collaborationImage from "../../assets/crmImg.jpg";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import UseScrollAnimation from "../../hooks/UseScrollAnimation";
import { useTranslation } from "react-i18next";

const CRMPage = () => {
  const { t } = useTranslation();
  UseScrollAnimation();
  const howItWorksRef = useRef(null);

  const handleExploreClick = () => {
    if (howItWorksRef.current) {
      howItWorksRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const TypewriterTitle = () => {
    const textLines = t("crm_page.preview.typewriter", { returnObjects: true }) || ["Your Immigration Journey,", "Simplified"];
    const [displayed, setDisplayed] = useState(["", ""]);
    const [line, setLine] = useState(0);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
      if (line > 1) return;
      const current = textLines[line] || "";

      if (idx < current.length) {
        const t = setTimeout(() => {
          setDisplayed((prev) => {
            const next = [...prev];
            next[line] = (next[line] || "") + current.charAt(idx);
            return next;
          });
          setIdx((v) => v + 1);
        }, 70);
        return () => clearTimeout(t);
      } else if (line === 0) {
        const timer = setTimeout(() => {
          setLine(1);
          setIdx(0);
        }, 500);
        return () => clearTimeout(timer);
      } else {
      }
    }, [idx, line, textLines]);

    return (
      <motion.h1
        className="text-3xl md:text-4xl xl:text-5xl font-extrabold leading-tight whitespace-pre-line"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      >
        <span className="text-gray-900">{displayed[0]}</span>
        <br />
        <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
          {displayed[1]}
        </span>

        {/* Glossy Cursor */}
        <motion.span
          className="inline-block w-[2px] h-[1.2em] bg-blue-700 ml-1 align-middle"
          animate={{ opacity: [1, 0] }}
          transition={{ repeat: Infinity, duration: 0.8 }}
        />
      </motion.h1>
    );
  };

  // Steps Data with Translations
  const stepsData = t("crm_page.how_it_works.steps", { returnObjects: true });
  const steps = Array.isArray(stepsData) ? stepsData.map((step, index) => ({
    ...step,
    icon: (
      <span className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl font-semibold ${index === 0 ? "bg-blue-100 text-blue-600" :
          index === 1 ? "bg-indigo-100 text-indigo-600" :
            index === 2 ? "bg-sky-100 text-sky-600" :
              "bg-blue-100 text-blue-600"
        }`}>
        {index + 1}
      </span>
    ),
  })) : [];

  // Benefits Icons
  const benefitIcons = ["⚡", "🎯", "🔒", "💬", "📊"];
  const benefitsList = t("crm_page.benefits_data", { returnObjects: true });
  const benefits = Array.isArray(benefitsList) ? benefitsList.map((b, i) => ({
    ...b,
    icon: benefitIcons[i] || "✨"
  })) : [];

  return (
    <div className="min-h-screen bg-white text-gray-900 ">
      <section className="relative isolate overflow-hidden text-white py-24 md:py-32 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
        {/* Animated Background */}
        <div className="absolute inset-0 -z-30 animate-[gradientMove_16s_ease_infinite] bg-[linear-gradient(120deg,#1e3a8a_0%,#1d4ed8_50%,#2563eb_100%)] bg-[length:200%_200%] opacity-95" />

        {/* Floating Orbs */}
        <div className="pointer-events-none absolute inset-0 -z-20">
          <span className="ball absolute left-[8%] top-[18%]" />
          <span className="ball absolute right-[12%] top-[28%] delay-300" />
          <span className="ball-sm absolute left-[16%] bottom-[20%] delay-700" />
          <span className="ball-sm absolute right-[24%] bottom-[26%] delay-500" />
          <span className="ball-sm absolute left-[32%] top-[36%] delay-1000" />
        </div>

        {/* Soft Glows */}
        <div
          aria-hidden
          className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-white/10 blur-3xl -z-10"
          style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
        />
        <div
          aria-hidden
          className="absolute -bottom-32 -right-32 h-[34rem] w-[34rem] rounded-full bg-white/10 blur-3xl -z-10"
          style={{ maskImage: "radial-gradient(closest-side, white, transparent)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="up" delay={0.1}>
            <div className="text-center">
              <motion.div
                className="inline-flex items-center gap-2 mb-5 rounded-full border border-white/20 bg-white/10 px-5 py-2 backdrop-blur-md"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-[pingSlow_2.4s_linear_infinite]" />
                <span className="text-sm font-semibold text-white/90">
                  {t("crm_page.hero.badge")}
                </span>
              </motion.div>

              <motion.h1
                className="text-5xl md:text-6xl font-extrabold tracking-tight mb-4 drop-shadow-[0_6px_24px_rgba(0,0,0,.35)]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                style={{
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #e2e8f0 60%, rgba(255,255,255,.9) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("crm_page.hero.title")}
              </motion.h1>

              {/* Glowing Line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.45, duration: 0.7 }}
                className="relative mx-auto mt-4 h-[3px] w-48 origin-center rounded-full bg-gradient-to-r from-cyan-300 via-white to-blue-300 shadow-[0_0_18px_rgba(255,255,255,.6)] overflow-hidden"
              >
                <div className="absolute inset-0 shimmer opacity-80" />
                <div className="absolute -inset-1 bg-white/40 blur-md glowPulse rounded-full" />
              </motion.div>

              <motion.h2
                className="text-2xl md:text-3xl font-semibold mb-6 text-blue-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                {t("crm_page.hero.subtitle")}
              </motion.h2>

              <motion.p
                className="text-xl text-blue-100/90 max-w-3xl mx-auto mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                {t("crm_page.hero.description")}
              </motion.p>

              <motion.div
                className="flex flex-wrap justify-center gap-3 sm:gap-4 text-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                <div className="bg-white/10 border border-white/15 backdrop-blur-sm rounded-full px-4 py-2">
                  <span className="font-semibold">{t("crm_page.hero.stats.secure_messaging")}</span>
                </div>
                <div className="bg-white/10 border border-white/15 backdrop-blur-sm rounded-full px-4 py-2">
                  <span className="font-semibold">{t("crm_page.hero.stats.role_based")}</span>
                </div>
                <div className="bg-white/10 border border-white/15 backdrop-blur-sm rounded-full px-4 py-2">
                  <span className="font-semibold">{t("crm_page.hero.stats.timeline")}</span>
                </div>
              </motion.div>
            </div>
          </ScrollAnimation>
        </div>

        <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none -z-10">
          <svg
            viewBox="0 0 1440 240"
            className="w-full h-[100px] md:h-[140px]"
            preserveAspectRatio="none"
          >
            <path
              className="animate-[waveDrift_14s_ease_in_out_infinite]"
              fill="rgba(255,255,255,0.20)"
              d="M0,140 C180,160 360,120 540,140 C720,160 900,200 1080,170 C1260,140 1350,120 1440,130 L1440,240 L0,240 Z"
            />
            <path
              className="animate-[waveDrift_12s_ease_in_out_infinite_reverse]"
              fill="rgba(255,255,255,0.28)"
              d="M0,150 C200,130 420,170 640,155 C860,140 1000,150 1220,160 C1330,165 1385,165 1440,166 L1440,240 L0,240 Z"
            />
            <path
              className="animate-[waveDrift_10s_ease_in_out_infinite]"
              fill="rgba(255,255,255,0.36)"
              d="M0,165 C240,170 420,160 620,168 C820,176 1040,154 1240,162 C1340,166 1390,166 1440,166 L1440,240 L0,240 Z"
            />
          </svg>
        </div>
      </section>


      <SeoHead
        title={t("crm_page.hero.title") + " | Citizen Hub"}
        description={t("crm_page.hero.description")}
        canonicalUrl="https://citizenhubimmigration.com/crm"
        keywords="immigration CRM, immigration case management, USCIS forms, lawyer collaboration, legal CRM, immigration platform, secure immigration portal"
      />

      <main>
        <section className="relative bg-gradient-to-br from-white via-blue-50 to-white overflow-hidden py-24 md:py-28">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-10 right-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
            <div className="absolute -bottom-20 left-10 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <ScrollAnimation direction="left" delay={0.1}>
                <div className="space-y-6">
                  <motion.div
                    className="inline-flex items-center space-x-2 bg-blue-100/80 border border-blue-200 rounded-full px-4 py-1.5 text-xs font-semibold text-blue-700 uppercase tracking-wider"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                    <span>{t("crm_page.preview.badge")}</span>
                  </motion.div>

                  <TypewriterTitle />


                  <motion.p
                    className="text-base md:text-lg text-gray-600 leading-relaxed max-w-xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                  >
                    {t("crm_page.preview.description")}
                  </motion.p>

                  <motion.div
                    className="flex flex-col sm:flex-row gap-3 pt-2"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <button
                      type="button"
                      onClick={handleExploreClick}
                      aria-label={t("crm_page.preview.explore_button")}
                      className="group inline-flex items-center justify-center px-6 py-3 text-sm md:text-base font-semibold text-white bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                    >
                      {t("crm_page.preview.explore_button")}
                      <svg
                        className="w-4 h-4 ml-3 group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                    <LanguageLink
                      to="/forms"
                      aria-label={t("crm_page.preview.start_button")}
                      className="inline-flex items-center justify-center px-6 py-3 text-sm md:text-base font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-full hover:bg-blue-50 transition-all duration-300 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
                    >
                      {t("crm_page.preview.start_button")}
                    </LanguageLink>
                  </motion.div>

                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.6 }}
                  >
                    {[
                      { label: t("crm_page.preview.stats.workflows.label"), value: t("crm_page.preview.stats.workflows.value") },
                      { label: t("crm_page.preview.stats.lawyers.label"), value: t("crm_page.preview.stats.lawyers.value") },
                      { label: t("crm_page.preview.stats.vault.label"), value: t("crm_page.preview.stats.vault.value") },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl border border-blue-100 bg-white/70 px-4 py-3 shadow-sm backdrop-blur-sm"
                      >
                        <div className="text-sm font-semibold text-blue-600">{item.value}</div>
                        <div className="text-xs text-gray-500 mt-1">{item.label}</div>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </ScrollAnimation>

              <ScrollAnimation direction="right" delay={0.2}>
                <motion.div
                  className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/60"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-transparent to-blue-700/10 z-10"></div>
                  <img
                    src={heroImage}
                    alt="Immigration professionals collaborating"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = collaborationImage; }}
                  />
                  <motion.div
                    className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-lg px-5 py-4 border border-blue-100 w-[85%] md:w-3/4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t("crm_page.preview.card.title")}</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{t("crm_page.preview.card.status")}</p>
                      </div>
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 text-blue-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M12 6v6l4 2m4 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-xs text-blue-600 font-semibold">{t("crm_page.preview.card.next_step")}</div>
                      <span className="text-xs text-gray-400">{t("crm_page.preview.card.due")}</span>
                    </div>
                  </motion.div>
                </motion.div>
              </ScrollAnimation>
            </div>
          </div>
        </section>

        <section
          ref={howItWorksRef}
          id="how-it-works"
          className="py-20 bg-white"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation direction="up" delay={0.1}>
              <div className="text-center mb-12">
                <motion.h2
                  className="text-3xl md:text-4xl font-bold text-gray-900"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {t("crm_page.how_it_works.title")}
                </motion.h2>
                <motion.p
                  className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                >
                  {t("crm_page.how_it_works.subtitle")}
                </motion.p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {steps.map((step, index) => (
                <ScrollAnimation key={index} direction="up" delay={index * 0.1}>
                  <motion.div
                    className="h-full rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-lg transition-all duration-300"
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex items-center space-x-4 mb-4">
                      {step.icon}
                      <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                  </motion.div>
                </ScrollAnimation>
              ))}
            </div>

            <ScrollAnimation direction="up" delay={0.4}>
              <motion.div
                className="mt-10 rounded-2xl border border-blue-100 bg-blue-50/70 px-6 py-5 text-center text-sm md:text-base text-blue-700 font-semibold"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {t("crm_page.how_it_works.note")}
              </motion.div>
            </ScrollAnimation>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-b from-blue-50 via-white to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <ScrollAnimation direction="left" delay={0.1}>
                <motion.div
                  className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/60"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-transparent to-blue-700/10 z-10" />
                  <img
                    src={collaborationImage}
                    alt="Lawyers collaborating through Citizen Hub"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = heroImage; }}
                  />
                  <div className="absolute top-6 left-6 bg-white/90 border border-blue-100 rounded-2xl px-4 py-3 shadow-md backdrop-blur-sm">
                    <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">
                      {t("crm_page.collaboration.timeline_card.title")}
                    </p>
                    <ul className="mt-2 space-y-2 text-xs text-gray-600">
                      {(t("crm_page.collaboration.timeline_card.items", { returnObjects: true }) || []).map((item, i) => (
                        <li key={i} className="flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </ScrollAnimation>

              <ScrollAnimation direction="right" delay={0.2}>
                <div className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <span className="inline-block rounded-full bg-blue-100 text-blue-600 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
                      {t("crm_page.collaboration.badge")}
                    </span>
                    <h2 className="mt-4 text-3xl md:text-4xl font-bold text-gray-900">
                      {t("crm_page.collaboration.title")}
                    </h2>
                  </motion.div>

                  <motion.div
                    className="space-y-4 text-gray-600 text-base leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                  >
                    {(t("crm_page.collaboration.description", { returnObjects: true }) || []).map((desc, i) => (
                      <p key={i}>{desc}</p>
                    ))}
                  </motion.div>

                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                  >
                    {(t("crm_page.collaboration.features", { returnObjects: true }) || []).map((feature) => (
                      <div
                        key={feature}
                        className="flex items-start space-x-3 rounded-2xl bg-white border border-blue-100 px-4 py-3 shadow-sm"
                      >
                        <span className="mt-1 w-2 h-2 bg-blue-600 rounded-full" />
                        <p className="text-sm text-gray-600">{feature}</p>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </ScrollAnimation>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation direction="up" delay={0.1}>
              <div className="text-center mb-12">
                <motion.h2
                  className="text-3xl md:text-4xl font-bold text-gray-900"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {t("crm_page.benefits.title")}
                </motion.h2>
                <motion.p
                  className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                >
                  {t("crm_page.benefits.subtitle")}
                </motion.p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <ScrollAnimation key={index} direction="up" delay={index * 0.1}>
                  <motion.div
                    className="h-full bg-white border border-gray-100 rounded-2xl px-6 py-6 shadow-sm hover:shadow-lg transition-all duration-300"
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 text-2xl mb-4">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
                  </motion.div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-20 bg-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/40 to-white pointer-events-none" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <ScrollAnimation direction="up" delay={0.1}>
              <motion.div
                className="rounded-3xl border border-blue-100 bg-white/80 backdrop-blur-md shadow-xl p-10 md:p-14"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="text-center">
                  <motion.h2
                    className="text-3xl md:text-4xl font-bold text-gray-900"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    {t("crm_page.future.title")}
                  </motion.h2>
                  <motion.p
                    className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                  >
                    {t("crm_page.future.description")}
                  </motion.p>
                </div>

                <motion.div
                  className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  {(t("crm_page.future.features", { returnObjects: true }) || []).map((item, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl border border-blue-100 bg-white px-5 py-4 shadow-sm"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 font-semibold">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <p className="text-sm font-semibold text-gray-700">{item}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </motion.div>
            </ScrollAnimation>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CRMPage;
